import { channel, buffers } from "redux-saga";
import { call, take, race, fork } from "redux-saga/effects";
import { Ice, Glacier2 } from "ice";
import { session } from "./actions";
import reducer from "./reducer";
import iceClientSaga from "./saga";
import { bindProxy, getGanymedeModule, logError } from "./utils";
export * from "./actions";
export * from "./reducer";
export { bindProxy } from "./utils";
function deferred() {
    let resolve, reject;
    const promise = new Promise((resolve_, reject_) => {
        resolve = resolve_;
        reject = reject_;
    });
    return { promise, resolve, reject };
}
export default function createIceClient({ iceConfig, propertyPrefix, proxies: proxiesConfig, tokenStorageKey = 'iceClientToken', }) {
    const { AccessTokenContextKey } = getGanymedeModule();
    const properties = Ice.createProperties();
    properties.setProperty('Ice.Default.Host', location.hostname);
    properties.setProperty('Ice.RetryIntervals', '-1');
    for (let key of Object.keys(iceConfig)) {
        properties.setProperty(key, iceConfig[key].toString());
    }
    const process = window.process;
    const argv = process ? process.argv : [];
    if (propertyPrefix != null) {
        properties.parseCommandLineOptions(propertyPrefix, argv);
    }
    const reconnectRequestChannel = channel(buffers.none());
    const loginRequestChannel = channel(buffers.none());
    const logoutRequestChannel = channel(buffers.none());
    const initData = new Ice.InitializationData();
    initData.properties = properties;
    // ice modifies args array so it must be copied
    let communicator = Ice.initialize([...argv], initData);
    let proxies = {};
    let activeDeferred = deferred();
    let _router = null;
    let _session = null;
    let _token = localStorage.getItem(tokenStorageKey);
    let _category = null;
    let _adapter = null;
    function updateProxies() {
        proxies = {};
        for (const name of Object.keys(proxiesConfig)) {
            const { property, get, type, authenticate = true, nullable = false, } = proxiesConfig[name];
            let prx;
            if (property) {
                prx = communicator.propertyToProxy(property);
            }
            else if (get) {
                prx = get(communicator);
            }
            else {
                throw new Error(`Either 'property' or 'get' must be specified ` +
                    `for '${name}' proxy config`);
            }
            if (prx == null) {
                if (nullable) {
                    proxies[name] = null;
                    continue;
                }
                else {
                    throw new Error(`Missing configuration property ${property}`);
                }
            }
            let proxy = type.uncheckedCast(prx);
            if (authenticate) {
                const context = new Map(proxy.ice_getContext());
                context.set(AccessTokenContextKey, _token);
                proxy = proxy.ice_context(context);
            }
            proxies[name] = bindProxy(proxy);
        }
    }
    let helper;
    helper = {
        reconnectRequestChannel,
        loginRequestChannel,
        logoutRequestChannel,
        getRouter() {
            if (_router)
                return Promise.resolve(_router);
            const router = Glacier2.RouterPrx
                .uncheckedCast(communicator.getDefaultRouter());
            if (router == null)
                throw new Error('Ice.Default.Router property not set');
            return router.getACMTimeout().then(timeout => {
                const connection = router.ice_getCachedConnection();
                if (timeout > 0)
                    connection.setACM(timeout, undefined, Ice.ACMHeartbeat.HeartbeatAlways);
                _router = bindProxy(router);
                return _router;
            });
        },
        resetRouter() {
            _router = null;
            _adapter = null;
            _category = null;
            activeDeferred.reject();
            activeDeferred = deferred();
            proxies = {};
            communicator.destroy().catch(error => {
                logError("Failed to destroy communicator", error, 'warn');
            });
            // ice modifies args array so it must be copied
            communicator = Ice.initialize([...argv], initData);
        },
        getToken() {
            return _token;
        },
        setToken(token) {
            localStorage.setItem(tokenStorageKey, token);
            _token = token;
            updateProxies();
        },
        removeToken() {
            localStorage.removeItem(tokenStorageKey);
            _token = null;
        },
        setSessionData({ router, session, token, category, adapter }) {
            _router = router;
            _session = session;
            _category = category;
            _adapter = adapter;
            helper.setToken(token);
            activeDeferred.resolve();
        },
        destroySession() {
            localStorage.removeItem(tokenStorageKey);
            _token = null;
            _session = null;
            _router.destroySession().catch(error => {
                console.warn('Failed to destroy session', error.toString(), error);
            });
        },
        logout() {
            localStorage.removeItem(tokenStorageKey);
            _session.logout().catch(error => {
                console.warn('Failed to log out', error.toString(), error);
            });
            _token = null;
            _session = null;
        },
        getSession() {
            return _session;
        },
        waitForConnectionClose() {
            const connection = _router.ice_getCachedConnection();
            return connection.waitUntilFinished().then(() => true);
        },
    };
    function isActive() {
        return _session != null;
    }
    function* sessionWrapper(fn, ...args) {
        if (!(yield call(isActive)))
            yield take(session.started.type);
        while (true) {
            yield race({
                call: call(fn, ...args),
                close: take([session.done.type, session.failed.type]),
            });
            yield take(session.started.type);
        }
    }
    return {
        login(payload) {
            loginRequestChannel.put(payload);
        },
        logout() {
            logoutRequestChannel.put(true);
        },
        retryConnection() {
            reconnectRequestChannel.put(true);
        },
        runWhenActive(...args) {
            return fork(sessionWrapper, ...args);
        },
        getNullableProxy(name) {
            if (proxiesConfig[name] == null)
                throw new Error(`Unknown proxy name: ${name}`);
            return activeDeferred.promise.then(() => proxies[name]);
        },
        getProxy(name) {
            if (proxiesConfig[name] == null)
                throw new Error(`Unknown proxy name: ${name}`);
            return activeDeferred.promise.then(() => {
                const proxy = proxies[name];
                if (proxy == null)
                    throw new Error(`Proxy is null: ${name}`);
                return proxy;
            });
        },
        createProxy(proxyString, type, authenticate) {
            let prx = communicator.stringToProxy(proxyString);
            if (prx == null) {
                throw new Error(`Got null proxy for string '${proxyString}'`);
            }
            let proxy = type.uncheckedCast(prx);
            if (authenticate) {
                const context = new Map(proxy.ice_getContext());
                context.set(AccessTokenContextKey, _token);
                proxy = proxy.ice_context(context);
            }
            return bindProxy(proxy);
        },
        addServant(prx, servant, name = Ice.generateUUID()) {
            if (_session == null)
                throw new Error('Session not active');
            const identity = new Ice.Identity(name, _category);
            return prx.uncheckedCast(_adapter.add(servant, identity));
        },
        removeServant(identity) {
            if (_adapter == null)
                return;
            try {
                _adapter.remove(identity);
            }
            catch (e) {
                // ignore
            }
        },
        getProperties() {
            return properties;
        },
        reducer,
        saga(options = {}) {
            return iceClientSaga(options, helper);
        },
    };
}



//////////////////
// WEBPACK FOOTER
// ./~/@neolab/ice-client/es/index.js
// module id = 140
// module chunks = 0